import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Item } from '../../services/api/itemMasterService';
import {
  fetchItemList,
  fetchItemDetail,
  fetchItemAlternateKeys,
  patchItem,
  searchItems,
} from '../thunks/itemMasterThunks';

export interface ItemMasterFilters {
  q: string;
  category: string;
  hasDimensions: boolean | null;     // null = no filter
  hasAlternateKeyType: string;
  hasAlternateKeyValue: string;
  // Spec pack-analysis-prepack-item-master Req 13.4 — kind filter.
  // '' = default (BE applies SKU-only). 'PACK' = PACK only. 'ALL' = no kind filter.
  itemType: string;
}

export interface ItemMasterState {
  list: {
    items: Item[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    error: string | null;
  };
  detail: Record<number, Item | null>;
  search: {
    q: string;
    results: Item[];
    isLoading: boolean;
  };
  filters: ItemMasterFilters;
}

const initialState: ItemMasterState = {
  list: {
    items: [],
    pageIndex: 1,
    pageSize: 50,
    totalCount: 0,
    isLoading: false,
    error: null,
  },
  detail: {},
  search: {
    q: '',
    results: [],
    isLoading: false,
  },
  filters: {
    q: '',
    category: '',
    hasDimensions: null,
    hasAlternateKeyType: '',
    hasAlternateKeyValue: '',
    // '' → undefined in loadList; BE applies its default-SKU contract.
    itemType: '',
  },
};

const itemMasterSlice = createSlice({
  name: 'itemMaster',
  initialState,
  reducers: {
    clearError: (state) => {
      state.list.error = null;
    },
    setFilter: <K extends keyof ItemMasterFilters>(
      state: ItemMasterState,
      action: PayloadAction<{ key: K; value: ItemMasterFilters[K] }>
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    clearFilters: (state) => {
      state.filters = { ...initialState.filters };
    },
    // Page-size selector (ListPagination). Resets to page 1 so the new size
    // starts from the top; the fetch echoes both back into the list state.
    setListPageSize: (state, action: PayloadAction<number>) => {
      state.list.pageSize = action.payload;
      state.list.pageIndex = 1;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.search.q = action.payload;
    },
    clearSearchResults: (state) => {
      state.search.results = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItemList.pending, (state) => {
        state.list.isLoading = true;
        state.list.error = null;
      })
      .addCase(fetchItemList.fulfilled, (state, action) => {
        state.list.isLoading = false;
        state.list.items = action.payload.items;
        state.list.pageIndex = action.payload.pageIndex;
        state.list.pageSize = action.payload.pageSize;
        state.list.totalCount = action.payload.totalCount;
      })
      .addCase(fetchItemList.rejected, (state, action) => {
        state.list.isLoading = false;
        state.list.error = action.payload as string;
      });

    builder
      .addCase(fetchItemDetail.fulfilled, (state, action) => {
        state.detail[action.payload.id] = action.payload;
      });

    builder
      .addCase(fetchItemAlternateKeys.fulfilled, (state, action) => {
        const itemId = action.meta.arg.itemId;
        const existing = state.detail[itemId];
        if (existing) {
          existing.alternateKeys = action.payload.items;
        }
      });

    builder
      .addCase(patchItem.fulfilled, (state, action) => {
        const updated = action.payload;
        state.detail[updated.id] = updated;
        const idx = state.list.items.findIndex((x) => x.id === updated.id);
        if (idx >= 0) {
          // Preserve alternateKeys from the list row if patch response trimmed them.
          state.list.items[idx] = { ...state.list.items[idx], ...updated };
        }
      });

    builder
      .addCase(searchItems.pending, (state) => {
        state.search.isLoading = true;
      })
      .addCase(searchItems.fulfilled, (state, action) => {
        state.search.isLoading = false;
        state.search.results = action.payload;
      })
      .addCase(searchItems.rejected, (state) => {
        state.search.isLoading = false;
      });
  },
});

export const { clearError, setFilter, clearFilters, setListPageSize, setSearchQuery, clearSearchResults } =
  itemMasterSlice.actions;
export default itemMasterSlice.reducer;
